import React from 'react';
import { features } from '../mock';
import { Card, CardContent } from '../components/ui/card';
import { Mic, MessageCircle, Globe, Users, Shield, Zap, Phone, Code, BarChart3, Lock } from 'lucide-react';

const iconMap = {
  Mic: Mic,
  MessageCircle: MessageCircle,
  Globe: Globe,
  Users: Users,
  Shield: Shield,
  Zap: Zap
};

export const Features = () => {
  return (
    <div className="min-h-screen pt-24 pb-16">
      {/* Hero Section */}
      <section className="px-4 sm:px-6 lg:px-8 py-16 bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-5xl font-bold text-gray-900 mb-6">
            Voice AI Features
            <span className="block mt-2 bg-gradient-to-r from-purple-600 via-pink-500 to-blue-600 bg-clip-text text-transparent">
              Built for Modern Businesses
            </span>
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Powered by OpenAI's industry-leading real-time APIs, Nyhive delivers natural, intelligent voice experiences 
            with enterprise-grade reliability and security.
          </p>
        </div>
      </section>

      {/* Main Features Grid */}
      <section className="px-4 sm:px-6 lg:px-8 py-20 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature) => {
              const Icon = iconMap[feature.icon];
              return (
                <Card key={feature.id} className="border-2 hover:border-purple-300 transition-all hover:shadow-xl">
                  <CardContent className="p-8">
                    <div className="w-14 h-14 bg-gradient-to-br from-purple-100 to-pink-100 rounded-xl flex items-center justify-center mb-6">
                      <Icon className="text-purple-600" size={28} />
                    </div>
                    <h3 className="text-2xl font-semibold text-gray-900 mb-3">{feature.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Technical Capabilities */}
      <section className="px-4 sm:px-6 lg:px-8 py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-16">Technical Excellence</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {/* Left Column */}
            <div className="space-y-8">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Phone className="text-purple-600" size={24} />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Telephony Integration</h3>
                  <p className="text-gray-600">Seamlessly integrate with Twilio and other telephony providers for voice calls.</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-pink-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Code className="text-pink-600" size={24} />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Developer-Friendly APIs</h3>
                  <p className="text-gray-600">Simple REST APIs and comprehensive SDKs with detailed documentation.</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Lock className="text-blue-600" size={24} />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Advanced Security</h3>
                  <p className="text-gray-600">End-to-end encryption, SOC 2 compliance, and data privacy controls.</p>
                </div>
              </div>
            </div>
            
            {/* Right Column */}
            <div className="space-y-8">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <BarChart3 className="text-purple-600" size={24} />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Real-Time Analytics</h3>
                  <p className="text-gray-600">Monitor voice interactions, track performance, and gain insights instantly.</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-pink-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Users className="text-pink-600" size={24} />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Custom Voice Personas</h3>
                  <p className="text-gray-600">Create unique AI personalities that match your brand voice and tone.</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Globe className="text-blue-600" size={24} />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Global Scale</h3>
                  <p className="text-gray-600">99.9% uptime SLA with infrastructure spanning multiple regions worldwide.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="px-4 sm:px-6 lg:px-8 py-20 bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-4">Use Cases</h2>
          <p className="text-center text-xl text-gray-600 mb-16">Voice AI solutions for every industry</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Card className="border-2 hover:shadow-xl transition-all">
              <CardContent className="p-6 text-center">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Customer Support</h3>
                <p className="text-gray-600">24/7 intelligent voice support that understands and resolves customer issues.</p>
              </CardContent>
            </Card>
            
            <Card className="border-2 hover:shadow-xl transition-all">
              <CardContent className="p-6 text-center">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Healthcare</h3>
                <p className="text-gray-600">Patient support, appointment scheduling, and health information delivery.</p>
              </CardContent>
            </Card>
            
            <Card className="border-2 hover:shadow-xl transition-all">
              <CardContent className="p-6 text-center">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">E-Commerce</h3>
                <p className="text-gray-600">Voice-powered shopping assistants and order management systems.</p>
              </CardContent>
            </Card>
            
            <Card className="border-2 hover:shadow-xl transition-all">
              <CardContent className="p-6 text-center">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Education</h3>
                <p className="text-gray-600">Interactive learning experiences with natural voice conversations.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};